'use client';

/**
 * The real Shopify cart, from inside the planner.
 *
 * In production the planner is served through the Shopify app proxy at
 * campingaustralia.com.au/apps/campsite-explorer, so the storefront's AJAX
 * cart API (/cart.js, /cart/add.js, /products/<handle>.js) is same-origin and
 * the shopper's own cart cookie rides along. Off the store domain (local dev)
 * the calls go to the absolute store URL and will usually be blocked by CORS;
 * callers surface that as an error instead of pretending it worked.
 */

import { trackActivity } from './activity';

export const STORE_ORIGIN = 'https://campingaustralia.com.au';

/** Event fired after every successful add — the header count and the toast both listen. */
export const CART_ADDED_EVENT = 'ca:cart-added';

export interface CartAddedDetail {
  title: string;
  variantTitle: string | null;
  image: string | null;
  price: number | null; // dollars
  cartCount: number;
  url: string;
}

export function storeUrl(path: string): string {
  if (typeof window !== 'undefined' && window.location.hostname.endsWith('campingaustralia.com.au')) {
    return path;
  }
  return `${STORE_ORIGIN}${path}`;
}

export async function fetchCartCount(): Promise<number> {
  const res = await fetch(storeUrl('/cart.js'), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  const cart = await res.json();
  return typeof cart.item_count === 'number' ? cart.item_count : 0;
}

export class CartError extends Error {
  constructor(public kind: 'sold_out' | 'unavailable' | 'network', message: string) {
    super(message);
  }
}

interface StoreVariant {
  id: number;
  title: string;
  available: boolean;
  price: number; // cents
  featured_image?: { src: string } | null;
}

/**
 * Add one unit of a product to the cart.
 * `variantId` is used when known; otherwise the product's first available
 * variant is looked up from /products/<handle>.js.
 */
export async function addToStoreCart(opts: {
  handle: string;
  variantId?: number | string | null;
  title?: string;
  image?: string | null;
}): Promise<CartAddedDetail> {
  let variant: StoreVariant | null = null;
  let productTitle = opts.title || '';
  let productImage = opts.image || null;
  let hasOnlyDefault = true;

  try {
    if (!opts.variantId) {
      const res = await fetch(storeUrl(`/products/${encodeURIComponent(opts.handle)}.js`), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.status === 404) throw new CartError('unavailable', 'This product is no longer in the store.');
      if (!res.ok) throw new CartError('network', `product ${res.status}`);
      const product = await res.json();
      productTitle = product.title || productTitle;
      productImage = productImage || product.featured_image || null;
      const variants: StoreVariant[] = product.variants || [];
      hasOnlyDefault = variants.length <= 1;
      variant = variants.find((v) => v.available) || null;
      if (!variant) throw new CartError('sold_out', 'Sold out right now.');
    }

    const id = variant ? variant.id : Number(opts.variantId);
    const add = await fetch(storeUrl('/cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
    });
    if (add.status === 422) {
      const body = await add.json().catch(() => ({}));
      throw new CartError('sold_out', body.description || 'We can’t add any more of this one.');
    }
    if (!add.ok) throw new CartError('network', `cart/add ${add.status}`);
    const added = await add.json().catch(() => ({}));
    const line = Array.isArray(added.items) ? added.items[0] : added;

    let cartCount = 0;
    try {
      cartCount = await fetchCartCount();
    } catch {
      /* the add worked; the count will refresh on next page */
    }

    const detail: CartAddedDetail = {
      title: line?.product_title || productTitle,
      variantTitle: !hasOnlyDefault && variant ? variant.title : null,
      image: line?.image || productImage,
      price: typeof line?.final_price === 'number' ? line.final_price / 100 : variant ? variant.price / 100 : null,
      cartCount,
      url: `/products/${opts.handle}`,
    };

    trackActivity('cart_add', {
      name: detail.title,
      value: detail.price,
      qty: 1,
      meta: { handle: opts.handle, variant: String(id) },
    });

    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cartCount } }));
    window.dispatchEvent(new CustomEvent<CartAddedDetail>(CART_ADDED_EVENT, { detail }));
    return detail;
  } catch (err) {
    if (err instanceof CartError) throw err;
    throw new CartError('network', 'Couldn’t reach the cart. Check your connection and try again.');
  }
}
